import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BRANDING_THEMES } from "../data";

const STORAGE_KEY = "ho-branding-theme";

interface HeadOfficeThemeValue {
  /** index into BRANDING_THEMES */
  themeIdx: number;
  /** the theme key ("" | "teal" | "dark-slate" ...) */
  themeKey: string;
  setThemeIdx: (idx: number) => void;
}

const HeadOfficeThemeContext = createContext<HeadOfficeThemeValue>({
  themeIdx: 0,
  themeKey: "",
  setThemeIdx: () => {},
});

/** map a theme key onto the `data-ho-theme` attribute value */
export const attrFor = (key: string) => (key === "" ? "aubergine" : key);

/**
 * Holds the head-office branding theme so the header colour panel and the
 * dashboard content stay in sync, and persists the choice per browser. The
 * `data-ho-theme` attribute is applied by the dashboard container itself (see
 * HeadOfficeDashboard) so the CSS variables in `theme.module.scss` scope to it.
 */
export function HeadOfficeThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [themeIdx, setThemeIdxState] = useState(0);

  // hydrate from localStorage once on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        const idx = BRANDING_THEMES.findIndex((t) => t.key === stored);
        if (idx >= 0) setThemeIdxState(idx);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const themeKey = BRANDING_THEMES[themeIdx]?.key ?? "";

  const setThemeIdx = useCallback((idx: number) => {
    setThemeIdxState(idx);
    try {
      localStorage.setItem(STORAGE_KEY, BRANDING_THEMES[idx]?.key ?? "");
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({ themeIdx, themeKey, setThemeIdx }),
    [themeIdx, themeKey, setThemeIdx],
  );

  return (
    <HeadOfficeThemeContext.Provider value={value}>
      {children}
    </HeadOfficeThemeContext.Provider>
  );
}

export const useHeadOfficeTheme = () => useContext(HeadOfficeThemeContext);
